import { useMediaDisplayUrl } from '@/lib/mediaDisplay';
import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { notifyMediaChange } from '@/lib/mediaEffects';
import { useQueryClient } from '@tanstack/react-query';
import { MediaFolder, mediaApi, resolveMediaUrl, authenticatedMediaUrl, isTauri } from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { alertDialog } from '@/lib/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const OUTPUT_MAX_WIDTH = 1600;
/** Smallest crop box, in on-screen stage pixels. */
const MIN = 32;

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

type Rect = { x: number; y: number; w: number; h: number };
type Bounds = { left: number; top: number; right: number; bottom: number };

/** Which edges a handle drags. Corners set two, edges set one. */
const HANDLES = {
  nw: { l: true, t: true },
  n: { t: true },
  ne: { r: true, t: true },
  e: { r: true },
  se: { r: true, b: true },
  s: { b: true },
  sw: { l: true, b: true },
  w: { l: true },
} as const;
type HandleKey = keyof typeof HANDLES;

const HANDLE_META: { key: HandleKey; pos: CSSProperties; cursor: string }[] = [
  { key: 'nw', pos: { left: 0, top: 0 }, cursor: 'nwse-resize' },
  { key: 'n', pos: { left: '50%', top: 0 }, cursor: 'ns-resize' },
  { key: 'ne', pos: { left: '100%', top: 0 }, cursor: 'nesw-resize' },
  { key: 'e', pos: { left: '100%', top: '50%' }, cursor: 'ew-resize' },
  { key: 'se', pos: { left: '100%', top: '100%' }, cursor: 'nwse-resize' },
  { key: 's', pos: { left: '50%', top: '100%' }, cursor: 'ns-resize' },
  { key: 'sw', pos: { left: 0, top: '100%' }, cursor: 'nesw-resize' },
  { key: 'w', pos: { left: 0, top: '50%' }, cursor: 'ew-resize' },
];

type Edges = { l?: boolean; r?: boolean; t?: boolean; b?: boolean };
type Gesture = { mode: 'move' | HandleKey; startCrop: Rect; startX: number; startY: number };

/**
 * Recompute the crop rect for a handle drag. `px`/`py` are the pointer in stage
 * coords; `start` is the crop at gesture-start (its un-dragged edges stay fixed);
 * `b` is the image rect the box is clamped to. Free by default; when `aspect` is
 * set the box keeps that ratio, anchored at the opposite corner/edge.
 */
export function resizeCrop(
  edges: Edges,
  px: number,
  py: number,
  start: Rect,
  b: Bounds,
  aspect: number,
): Rect {
  let left = start.x;
  let top = start.y;
  let right = start.x + start.w;
  let bottom = start.y + start.h;
  const l = !!edges.l;
  const r = !!edges.r;
  const t = !!edges.t;
  const bo = !!edges.b;

  if (!aspect) {
    if (l) left = clamp(px, b.left, right - MIN);
    if (r) right = clamp(px, left + MIN, b.right);
    if (t) top = clamp(py, b.top, bottom - MIN);
    if (bo) bottom = clamp(py, top + MIN, b.bottom);
    return { x: left, y: top, w: right - left, h: bottom - top };
  }

  // Aspect-locked: size a ratio-correct box out from the fixed anchor.
  const anchorX = l ? right : r ? left : (left + right) / 2;
  const anchorY = t ? bottom : bo ? top : (top + bottom) / 2;
  const hEdge = l || r;
  const vEdge = t || bo;

  let w = 0;
  if (hEdge && vEdge) {
    w = Math.max(Math.abs(px - anchorX), Math.abs(py - anchorY) * aspect);
  } else if (hEdge) {
    w = Math.abs(px - anchorX);
  } else {
    w = Math.abs(py - anchorY) * aspect;
  }

  const availW = l
    ? anchorX - b.left
    : r
      ? b.right - anchorX
      : Math.min(anchorX - b.left, b.right - anchorX) * 2;
  const availH = t
    ? anchorY - b.top
    : bo
      ? b.bottom - anchorY
      : Math.min(anchorY - b.top, b.bottom - anchorY) * 2;

  w = clamp(w, MIN, availW);
  let h = w / aspect;
  if (h > availH) {
    h = availH;
    w = h * aspect;
  }
  const nx = l ? anchorX - w : r ? anchorX : anchorX - w / 2;
  const ny = t ? anchorY - h : bo ? anchorY : anchorY - h / 2;
  return { x: nx, y: ny, w, h };
}

interface ImageCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteSlug: string;
  folder: MediaFolder;
  /** The picked image URL (root-relative or absolute). */
  src: string;
  /** Final URL to store — the freshly-uploaded crop, or the original when skipped. */
  onCropped: (url: string) => void;
  /** Optional locked width/height ratio (e.g. 16/9). Omit for a free crop. */
  aspect?: number;
}

/**
 * Reframe a chosen feature image before it's saved (AB#612), Lightroom-style: the
 * image is shown fit-to-view and a resizable crop RECTANGLE is dragged over it —
 * 8 handles (corners + edges) resize, the interior drags the whole box. The box is
 * clamped to the image and never smaller than MIN. Default crop is free-form; pass
 * `aspect` to lock a ratio. "Crop" renders only the selected region to a canvas at
 * native resolution and re-uploads it through the same media path as ImageInsertDialog.
 *
 * ponytail: no crop dep is installed (react-easy-crop / react-image-crop), so this
 * stays an in-house canvas crop rather than a new dependency. Output is JPEG q0.9 —
 * feature heroes are photos, transparency isn't needed.
 *
 * AB#629: on desktop (Tauri) the admin runs from tauri://localhost while the media
 * lives on https://app.wordpuppi.com — a cross-origin <img> taints the canvas so
 * toBlob throws. We fetch the image BYTES through the shared client's transport
 * (native plugin-http under Tauri, CORS-immune; plain same-origin fetch on web),
 * wrap them in a blob: object URL (same-origin → untainted canvas), and draw from
 * that. The tainted-canvas catch stays as a last-resort fallback if the byte fetch
 * itself fails (then the ORIGINAL, uncropped URL is kept and the user is told).
 */
export function ImageCropDialog({
  open,
  onOpenChange,
  siteSlug,
  folder,
  src,
  onCropped,
  aspect,
}: ImageCropDialogProps) {
  const queryClient = useQueryClient();
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [stage, setStage] = useState({ w: 0, h: 0 });
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [crop, setCrop] = useState<Rect>({ x: 0, y: 0, w: 0, h: 0 });
  const [saving, setSaving] = useState(false);
  const [blobUrl, setBlobUrl] = useState('');

  const displaySrc = src ? resolveMediaUrl(src) : '';
  const protectedMedia = !!authenticatedMediaUrl(src);
  const authenticatedSrc = useMediaDisplayUrl(src, siteSlug, open);

  // AB#629: pull the image bytes through the shared client's transport (native
  // plugin-http under Tauri — CORS-immune; plain same-origin fetch on web) and
  // hand the <img> a same-origin blob: URL, so drawImage → toBlob never taints.
  // Only external public images retain the direct-URL fallback. Protected media
  // uses the authenticated hook above and never falls back after denial.
  useEffect(() => {
    if (!open || !displaySrc || protectedMedia) return;
    let revoked = false;
    let created = '';
    void (async () => {
      try {
        const doFetch = isTauri() ? (await import('@tauri-apps/plugin-http')).fetch : fetch;
        const res = await doFetch(displaySrc);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        const type = res.headers.get('content-type') || 'image/jpeg';
        const url = URL.createObjectURL(new Blob([buf], { type }));
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setBlobUrl(url);
      } catch (err) {
        console.warn('Crop preview blob fetch failed, using direct src:', err);
      }
    })();
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
      setBlobUrl('');
    };
  }, [open, displaySrc, protectedMedia]);

  // Fit-to-contain: the image rect inside the stage (letterboxed, centered).
  const fit =
    nat.w && nat.h && stage.w && stage.h ? Math.min(stage.w / nat.w, stage.h / nat.h) : 0;
  const dispW = nat.w * fit;
  const dispH = nat.h * fit;
  const imgX = (stage.w - dispW) / 2;
  const imgY = (stage.h - dispH) / 2;

  // Live geometry for the window pointer listeners (kept stable via refs).
  const gestureRef = useRef<Gesture | null>(null);
  const geomRef = useRef<Bounds>({ left: 0, top: 0, right: 0, bottom: 0 });
  const aspectRef = useRef(0);
  geomRef.current = { left: imgX, top: imgY, right: imgX + dispW, bottom: imgY + dispH };
  aspectRef.current = aspect ?? 0;

  const onWinMove = useCallback((e: PointerEvent) => {
    const g = gestureRef.current;
    const el = stageRef.current;
    if (!g || !el) return;
    const b = geomRef.current;
    if (g.mode === 'move') {
      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;
      setCrop({
        x: clamp(g.startCrop.x + dx, b.left, b.right - g.startCrop.w),
        y: clamp(g.startCrop.y + dy, b.top, b.bottom - g.startCrop.h),
        w: g.startCrop.w,
        h: g.startCrop.h,
      });
    } else {
      const rect = el.getBoundingClientRect();
      setCrop(
        resizeCrop(
          HANDLES[g.mode],
          e.clientX - rect.left,
          e.clientY - rect.top,
          g.startCrop,
          b,
          aspectRef.current,
        ),
      );
    }
  }, []);

  const onWinUp = useCallback(() => {
    gestureRef.current = null;
    window.removeEventListener('pointermove', onWinMove);
    window.removeEventListener('pointerup', onWinUp);
  }, [onWinMove]);

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', onWinMove);
      window.removeEventListener('pointerup', onWinUp);
    },
    [onWinMove, onWinUp],
  );

  const startGesture = (e: ReactPointerEvent, mode: 'move' | HandleKey) => {
    e.preventDefault();
    e.stopPropagation();
    gestureRef.current = { mode, startCrop: crop, startX: e.clientX, startY: e.clientY };
    window.addEventListener('pointermove', onWinMove);
    window.addEventListener('pointerup', onWinUp);
  };

  const measureStage = useCallback(() => {
    const el = stageRef.current;
    if (el) setStage({ w: el.clientWidth, h: el.clientHeight });
  }, []);

  const onImageLoad = () => {
    const img = imgRef.current;
    if (img) setNat({ w: img.naturalWidth, h: img.naturalHeight });
    measureStage();
  };

  useEffect(() => {
    if (!open) return;
    measureStage();
    window.addEventListener('resize', measureStage);
    return () => window.removeEventListener('resize', measureStage);
  }, [open, measureStage]);

  // Reset the crop box to the full image (or a centered aspect-fit box) whenever
  // a new image opens or the stage is remeasured.
  useEffect(() => {
    if (!open || !stage.w || !stage.h || !nat.w || !nat.h) return;
    const f = Math.min(stage.w / nat.w, stage.h / nat.h);
    const dw = nat.w * f;
    const dh = nat.h * f;
    const ix = (stage.w - dw) / 2;
    const iy = (stage.h - dh) / 2;
    if (aspect) {
      let w = dw;
      let h = w / aspect;
      if (h > dh) {
        h = dh;
        w = h * aspect;
      }
      setCrop({ x: ix + (dw - w) / 2, y: iy + (dh - h) / 2, w, h });
    } else {
      setCrop({ x: ix, y: iy, w: dw, h: dh });
    }
  }, [open, src, stage.w, stage.h, nat.w, nat.h, aspect]);

  const useOriginal = () => {
    onCropped(src);
    onOpenChange(false);
  };

  const doCrop = () => {
    const img = imgRef.current;
    if (!img || !nat.w || !crop.w || !fit) return;

    // On-screen crop rect → source rect in natural image pixels.
    const scale = 1 / fit;
    const sx = clamp((crop.x - imgX) * scale, 0, nat.w);
    const sy = clamp((crop.y - imgY) * scale, 0, nat.h);
    const sw = Math.min(crop.w * scale, nat.w - sx);
    const sh = Math.min(crop.h * scale, nat.h - sy);

    const outW = Math.min(Math.round(sw), OUTPUT_MAX_WIDTH);
    const outH = Math.max(1, Math.round((outW * sh) / sw));

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

    setSaving(true);
    let blobResult: (blob: Blob | null) => void;
    const finished = new Promise<Blob | null>((resolve) => {
      blobResult = resolve;
    });
    try {
      // Tainted (cross-origin) canvas throws here synchronously.
      canvas.toBlob((b) => blobResult(b), 'image/jpeg', 0.9);
    } catch {
      setSaving(false);
      onCropped(src); // fall back to the original, uncropped image
      onOpenChange(false);
      void alertDialog("This image can't be cropped here, so the original was kept.");
      return;
    }

    void finished.then(async (blob) => {
      if (!blob) {
        setSaving(false);
        onCropped(src);
        onOpenChange(false);
        return;
      }
      const file = new File([blob], 'feature-image.jpg', { type: 'image/jpeg' });
      try {
        const res = await mediaApi.upload(siteSlug, file, folder);
        void notifyMediaChange(queryClient, siteSlug, res.data ? [res.data] : [], 'added').catch(() => {});
        onCropped(res.data!.url);
        onOpenChange(false);
      } catch (err) {
        console.error('Cropped upload failed:', err);
        await alertDialog('Saving the cropped image failed. Please try again.');
      } finally {
        setSaving(false);
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Crop feature image</DialogTitle>
        </DialogHeader>

        <div
          ref={stageRef}
          className="relative aspect-video w-full touch-none select-none overflow-hidden rounded-lg border border-border bg-muted"
        >
          {displaySrc && (
            <img
              ref={imgRef}
              src={protectedMedia ? authenticatedSrc : blobUrl || displaySrc}
              onLoad={onImageLoad}
              alt=""
              draggable={false}
              className="pointer-events-none absolute max-w-none select-none"
              style={{ width: dispW || undefined, height: dispH || undefined, left: imgX, top: imgY }}
            />
          )}

          {crop.w > 0 && (
            <div
              onPointerDown={(e) => startGesture(e, 'move')}
              className="absolute cursor-move touch-none"
              style={{
                left: crop.x,
                top: crop.y,
                width: crop.w,
                height: crop.h,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
              }}
            >
              <div className="pointer-events-none absolute inset-0 rounded-sm ring-1 ring-inset ring-white/90" />
              {/* Rule-of-thirds guides. */}
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-y-0 left-1/3 w-px bg-white/30" />
                <div className="absolute inset-y-0 left-2/3 w-px bg-white/30" />
                <div className="absolute inset-x-0 top-1/3 h-px bg-white/30" />
                <div className="absolute inset-x-0 top-2/3 h-px bg-white/30" />
              </div>
              {HANDLE_META.map(({ key, pos, cursor }) => (
                <div
                  key={key}
                  onPointerDown={(e) => startGesture(e, key)}
                  className="absolute size-3 touch-none rounded-full border border-white bg-primary shadow"
                  style={{ ...pos, cursor, transform: 'translate(-50%, -50%)' }}
                />
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Drag the corners or edges to resize the crop, or drag inside to move it.
        </p>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={useOriginal} disabled={saving}>
            Use original
          </Button>
          <Button type="button" onClick={doCrop} disabled={saving || !nat.w}>
            {saving ? 'Saving…' : 'Crop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
